import { SelectProps } from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'
import { DatasetListParams } from '@/api/datasetApi'
import {
  useGetDatasetList,
  useGetDatasetVersionsList
} from '@/queries/datasetQueries'
import { Dataset, DatasetVersion } from '@/types/dataset'
import { DatasetSelect } from './DatasetSelect'

type DatasetSelectionState = {
  datasetId: string | null
  versionId: string | null
}

export type DatasetSelectionValues = {
  dataset: Dataset | null
  version: DatasetVersion | null
}

type DatasetSelectHandlerProps = {
  initialSelectionState?: DatasetSelectionState
  listParams?: DatasetListParams
  inputProps: SelectProps
  preselectFirst?: boolean
  onChange: (selectionValues: DatasetSelectionValues) => void
}

/**
 * Handles dataset and version selection.
 * On dataset change, it fetches the latest version and calls `onChange`
 * with both the dataset and version. Version info is needed for API actions
 * like cropping or cloning.
 */
export const DatasetSelectHandler = ({
  initialSelectionState,
  listParams,
  inputProps,
  preselectFirst,
  onChange
}: DatasetSelectHandlerProps) => {
  const [selectionState, setSelectionState] = useState<DatasetSelectionState>(
    initialSelectionState || { datasetId: null, versionId: null }
  )

  const { data: datasetsData, isFetching: isFetchingDatasets } =
    useGetDatasetList(listParams)

  const datasets = useMemo(() => datasetsData?.results || [], [datasetsData])

  const { data: versionsData, isFetching: isFetchingVersions } =
    useGetDatasetVersionsList(
      selectionState.datasetId || '',
      {
        limit: 1, // we only need the most recent version
        ordering: '-created_at'
      },
      {
        enabled:
          selectionState.datasetId !== null && selectionState.versionId === null
      }
    )

  const versions = useMemo(() => versionsData?.results || [], [versionsData])

  useEffect(() => {
    if (preselectFirst && !selectionState.datasetId && datasets.length > 0) {
      setSelectionState({
        datasetId: datasets[0].id,
        versionId: null
      })
    }
  }, [preselectFirst, datasets, selectionState.datasetId])

  useEffect(() => {
    const mostRecentVersion = versions[0]

    if (
      mostRecentVersion &&
      selectionState.datasetId === mostRecentVersion.dataset && // make sure version belongs to the selected dataset
      selectionState.versionId !== mostRecentVersion.id // make sure we're not already on that version
    ) {
      setSelectionState({
        datasetId: selectionState.datasetId,
        versionId: mostRecentVersion.id
      })

      onChange({
        dataset:
          datasets.find((dataset) => dataset.id === selectionState.datasetId) ||
          null,
        version: mostRecentVersion
      })
    }
  }, [versions, selectionState, onChange, datasets])

  const handleDatasetChange = (datasetId: string) => {
    setSelectionState({ datasetId, versionId: null })
  }

  return (
    <DatasetSelect
      {...inputProps}
      value={selectionState.datasetId}
      datasets={datasets}
      isLoading={isFetchingDatasets || isFetchingVersions}
      onChange={(value) => {
        handleDatasetChange(value as string)
      }}
    />
  )
}
